/**
 * Sound levels for voice notes: loudness sampled from an AnalyserNode, and a
 * run of levels reduced to a fixed number of bars.
 */

/** Peak of each of `count` buckets across `levels`. */
export const summariseLevels = (
  levels: readonly number[],
  count: number,
): number[] => {
  if (levels.length === 0 || count <= 0) return [];
  const bucketSize = levels.length / count;
  return Array.from({ length: count }, (_, i) => {
    const from = Math.floor(i * bucketSize);
    const to = Math.max(from + 1, Math.floor((i + 1) * bucketSize));
    return Math.max(...levels.slice(from, to));
  });
};

/** The last `count` levels, front-padded with silence. */
export const latestLevels = (
  levels: readonly number[],
  count: number,
): number[] => {
  const recent = levels.slice(-count);
  return [...new Array<number>(count - recent.length).fill(0), ...recent];
};

// Speech is quiet relative to full scale; boost before clamping.
const RMS_GAIN = 4;
const BYTE_MIDPOINT = 128;

/** Loudness of one frame of byte time-domain samples, 0–1. */
export const rmsLevel = (samples: ArrayLike<number>): number => {
  if (samples.length === 0) return 0;
  let sum = 0;
  for (let i = 0; i < samples.length; i += 1) {
    const v = (samples[i] - BYTE_MIDPOINT) / BYTE_MIDPOINT;
    sum += v * v;
  }
  return Math.min(1, Math.sqrt(sum / samples.length) * RMS_GAIN);
};
